import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Plus, Trash2, GripVertical, Loader2, Upload, Image, X, Tag, ChevronUp, ChevronDown } from 'lucide-react';
import ThemePicker from '../components/ThemePicker';
import { withDefaults } from '../lib/themes';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SurveyEditor = () => {
  const { surveyId } = useParams();
  const navigate = useNavigate();
  const isEditing = !!surveyId;

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [canModify, setCanModify] = useState(true); // owners/admins can edit groups; group-members can't
  const [survey, setSurvey] = useState({
    name: '',
    description: '',
    active: true,
    kiosk_restart_seconds: 0,
    questions: [],
    group_ids: [],
    theme: withDefaults({}),
    report_schedule: {
      enabled: false,
      frequency: 'weekly',
      recipients: [],
      day_of_week: 0,
      day_of_month: 1,
      hour: 9
    }
  });

  const [newRecipient, setNewRecipient] = useState('');
  const [notifyGroups, setNotifyGroups] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const reorderQuestions = (from, to) => {
    if (from === to || from == null || to == null) return;
    const next = [...survey.questions];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setSurvey({ ...survey, questions: next });
  };

  const onDragStart = (e, idx) => {
    setDragIndex(idx);
    e.dataTransfer.effectAllowed = 'move';
    // Some browsers require text data on the dataTransfer to fire drop events
    try { e.dataTransfer.setData('text/plain', String(idx)); } catch (_) {}
  };

  const onDragOver = (e, idx) => {
    e.preventDefault();
    if (dragOverIndex !== idx) setDragOverIndex(idx);
    e.dataTransfer.dropEffect = 'move';
  };

  const onDrop = (e, idx) => {
    e.preventDefault();
    reorderQuestions(dragIndex, idx);
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const onDragEnd = () => {
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const moveQuestion = (from, dir) => {
    const to = from + dir;
    if (to < 0 || to >= survey.questions.length) return;
    reorderQuestions(from, to);
  };

  useEffect(() => {
    fetchGroups();
    if (isEditing) {
      fetchSurvey();
    }
  }, [surveyId]);

  const fetchGroups = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/groups`, { withCredentials: true });
      setAvailableGroups(data);
    } catch (error) {
      // Non-fatal; group picker will just be empty
      console.error('Error fetching groups:', error);
    }
  };

  const fetchSurvey = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/surveys/${surveyId}`, { withCredentials: true });
      setSurvey({
        ...data,
        group_ids: data.group_ids || [],
        kiosk_restart_seconds: data.kiosk_restart_seconds || 0,
        theme: withDefaults(data.theme || {}),
        report_schedule: data.report_schedule || survey.report_schedule
      });
      setCanModify(data.can_modify !== false);
    } catch (error) {
      alert('Error loading survey');
      navigate('/admin');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      alert('Logo is larger than 5 MB. Please pick a smaller image.');
      e.target.value = '';
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      // IMPORTANT: do NOT set Content-Type manually here.  Axios auto-generates
      // `multipart/form-data; boundary=...`; overriding it strips the boundary
      // and the backend then rejects the body with 422.
      const { data } = await axios.post(`${API_URL}/api/upload`, formData, {
        withCredentials: true,
      });
      setSurvey({
        ...survey,
        theme: { ...survey.theme, logo_url: data.url }
      });
    } catch (error) {
      const detail = error?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (error?.message || 'Unknown error');
      alert(`Couldn't upload logo: ${msg}`);
    } finally {
      setUploading(false);
      e.target.value = ''; // allow re-selecting the same file
    }
  };

  const addQuestion = (type) => {
    const newQuestion = {
      id: `q_${Date.now()}`,
      type,
      question: '',
      required: true,
      options: (type === 'radio' || type === 'checkbox') ? [
        { value: 'yes', label: 'Yes' },
        { value: 'somewhat', label: 'Somewhat' },
        { value: 'no', label: 'No' }
      ] : null,
      placeholder: type === 'text' || type === 'textarea' ? 'Enter your response...' : null
    };
    setSurvey({ ...survey, questions: [...survey.questions, newQuestion] });
  };

  const updateQuestion = (index, updates) => {
    const newQuestions = [...survey.questions];
    newQuestions[index] = { ...newQuestions[index], ...updates };
    setSurvey({ ...survey, questions: newQuestions });
  };

  const removeQuestion = (index) => {
    setSurvey({ ...survey, questions: survey.questions.filter((_, i) => i !== index) });
  };

  const updateOption = (qIndex, oIndex, updates) => {
    const newQuestions = [...survey.questions];
    newQuestions[qIndex].options[oIndex] = { ...newQuestions[qIndex].options[oIndex], ...updates };
    setSurvey({ ...survey, questions: newQuestions });
  };

  const addOption = (qIndex) => {
    const newQuestions = [...survey.questions];
    newQuestions[qIndex].options.push({ value: '', label: '' });
    setSurvey({ ...survey, questions: newQuestions });
  };

  const removeOption = (qIndex, oIndex) => {
    const newQuestions = [...survey.questions];
    newQuestions[qIndex].options = newQuestions[qIndex].options.filter((_, i) => i !== oIndex);
    setSurvey({ ...survey, questions: newQuestions });
  };

  const addRecipient = () => {
    if (newRecipient && !survey.report_schedule.recipients.includes(newRecipient)) {
      setSurvey({
        ...survey,
        report_schedule: {
          ...survey.report_schedule,
          recipients: [...survey.report_schedule.recipients, newRecipient]
        }
      });
      setNewRecipient('');
    }
  };

  const removeRecipient = (email) => {
    setSurvey({
      ...survey,
      report_schedule: {
        ...survey.report_schedule,
        recipients: survey.report_schedule.recipients.filter(r => r !== email)
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!survey.name.trim()) {
      alert('Please enter a survey name');
      return;
    }
    
    if (survey.questions.length === 0) {
      alert('Please add at least one question');
      return;
    }

    setSaving(true);
    try {
      const payload = { ...survey, notify_groups: notifyGroups };
      if (isEditing) {
        await axios.put(`${API_URL}/api/surveys/${surveyId}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/surveys`, payload, { withCredentials: true });
      }
      navigate('/admin');
    } catch (error) {
      alert(error.response?.data?.detail || 'Error saving survey');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#7A8B7A]" size={32} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Header */}
      <header className="bg-white border-b border-[#E5E0D8] sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <button
              onClick={() => navigate('/admin')}
              className="flex items-center gap-2 text-[#5C635A] hover:text-[#2C302E] transition-colors"
            >
              <ArrowLeft size={20} />
              Back to Dashboard
            </button>
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-[#7A8B7A] hover:bg-[#637363] text-white rounded-full transition-colors disabled:opacity-60"
              data-testid="save-survey-btn"
            >
              {saving && <Loader2 className="animate-spin" size={18} />}
              {isEditing ? 'Update Survey' : 'Create Survey'}
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-2xl font-medium text-[#2C302E] mb-8" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {isEditing ? 'Edit Survey' : 'Create New Survey'}
        </h1>

        <form onSubmit={handleSubmit}>
          {/* Basic Info */}
          <div className="bg-white rounded-xl border border-[#E5E0D8] p-6 mb-6">
            <h2 className="text-lg font-medium text-[#2C302E] mb-4">Basic Information</h2>
            
            <div className="grid gap-4">
              <div>
                <label className="block text-sm font-medium text-[#2C302E] mb-2">Survey Name *</label>
                <input
                  type="text"
                  value={survey.name}
                  onChange={(e) => setSurvey({ ...survey, name: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border border-[#E5E0D8] focus:outline-none focus:ring-2 focus:ring-[#7A8B7A]/20"
                  placeholder="e.g., Patient Satisfaction Survey"
                  required
                  data-testid="survey-name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#2C302E] mb-2">Description</label>
                <textarea
                  value={survey.description}
                  onChange={(e) => setSurvey({ ...survey, description: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border border-[#E5E0D8] focus:outline-none focus:ring-2 focus:ring-[#7A8B7A]/20 min-h-[80px]"
                  placeholder="Brief description of the survey..."
                  data-testid="survey-description"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="active"
                  checked={survey.active}
                  onChange={(e) => setSurvey({ ...survey, active: e.target.checked })}
                  className="w-4 h-4 rounded border-[#E5E0D8] text-[#7A8B7A] focus:ring-[#7A8B7A]"
                  data-testid="survey-active"
                />
                <label htmlFor="active" className="text-sm text-[#2C302E]">Survey is active</label>
              </div>

              {/* Kiosk mode — auto-return to a blank form after submission */}
              <div className="pt-2 border-t border-[#E5E0D8]">
                <label className="block text-sm font-medium text-[#2C302E] mb-2">
                  Kiosk mode — auto-restart after submission
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    min="0"
                    max="120"
                    value={survey.kiosk_restart_seconds}
                    onChange={(e) => setSurvey({
                      ...survey,
                      kiosk_restart_seconds: Math.max(0, Math.min(120, parseInt(e.target.value, 10) || 0)),
                    })}
                    className="w-24 px-4 py-2 rounded-lg border border-[#E5E0D8] focus:outline-none focus:ring-2 focus:ring-[#7A8B7A]/20"
                    data-testid="survey-kiosk-seconds"
                  />
                  <span className="text-sm text-[#5C635A]">seconds (0 = off)</span>
                </div>
                <p className="text-xs text-[#8E958C] mt-2">
                  {survey.kiosk_restart_seconds > 0
                    ? `After submitting, the thank-you screen shows a countdown and returns to a blank form after ${survey.kiosk_restart_seconds} second${survey.kiosk_restart_seconds === 1 ? '' : 's'}. Ideal for shared tablets.`
                    : 'Off — the thank-you screen stays on screen, with a "Start New Response" button.'}
                </p>
              </div>
            </div>
          </div>

          {/* Groups & Sharing */}
          <div className="bg-white rounded-xl border border-[#E5E0D8] p-6 mb-6">
            <div className="flex items-start justify-between gap-4 mb-1">
              <h2 className="text-lg font-medium text-[#2C302E]">Groups &amp; Sharing</h2>
              {!canModify && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-[#F4F1EA] text-[#5C635A]">Read-only</span>
              )}
            </div>
            <p className="text-sm text-[#5C635A] mb-4">
              Tag this survey with one or more groups. Every member of a tagged group will see
              this survey on their dashboard and can run reports from it (read-only).
              Leave empty to keep it visible only to you and admins.
            </p>

            {availableGroups.length === 0 ? (
              <p className="text-sm text-[#8E958C]" data-testid="no-groups-msg">
                No groups exist yet. Ask an admin to create one from the <strong>Groups</strong> tab.
              </p>
            ) : (
              <>
                {survey.group_ids.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3" data-testid="selected-group-chips">
                    {survey.group_ids.map(gid => {
                      const g = availableGroups.find(x => x.id === gid);
                      const label = g ? g.name : gid;
                      return (
                        <span
                          key={gid}
                          className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 rounded-full bg-[#7A8B7A]/10 text-[#637363] text-sm"
                          data-testid={`group-chip-${gid}`}
                        >
                          <Tag size={12} />
                          {label}
                          {canModify && (
                            <button
                              type="button"
                              onClick={() => setSurvey({ ...survey, group_ids: survey.group_ids.filter(x => x !== gid) })}
                              className="p-0.5 rounded-full hover:bg-[#7A8B7A]/20"
                              title="Remove tag"
                              data-testid={`remove-group-${gid}`}
                            >
                              <X size={12} />
                            </button>
                          )}
                        </span>
                      );
                    })}
                  </div>
                )}

                {canModify && (
                  <select
                    value=""
                    onChange={(e) => {
                      const gid = e.target.value;
                      if (gid && !survey.group_ids.includes(gid)) {
                        setSurvey({ ...survey, group_ids: [...survey.group_ids, gid] });
                      }
                    }}
                    className="w-full sm:w-72 px-3 py-2 rounded-lg border border-[#E5E0D8] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#7A8B7A]/20"
                    data-testid="add-group-select"
                  >
                    <option value="">+ Add group tag…</option>
                    {availableGroups
                      .filter(g => !survey.group_ids.includes(g.id))
                      .map(g => (
                        <option key={g.id} value={g.id}>{g.name}</option>
                      ))}
                  </select>
                )}

                {canModify && (
                  <label
                    className={`flex items-start gap-2 mt-4 ${survey.group_ids.length === 0 ? 'opacity-60' : ''}`}
                    title={survey.group_ids.length === 0 ? 'Pick a group tag first' : ''}
                  >
                    <input
                      type="checkbox"
                      checked={notifyGroups}
                      disabled={survey.group_ids.length === 0}
                      onChange={(e) => setNotifyGroups(e.target.checked)}
                      className="mt-0.5 w-4 h-4 rounded border-[#E5E0D8] text-[#7A8B7A] focus:ring-[#7A8B7A]"
                      data-testid="notify-groups-checkbox"
                    />
                    <span className="text-sm text-[#2C302E]">
                      Email members of {isEditing ? 'newly-added' : 'tagged'} groups when I save
                      <span className="block text-xs text-[#8E958C] mt-0.5">
                        {isEditing
                          ? "Sends a one-time heads-up to members of any group you've added since last save. Existing members aren't re-notified."
                          : "Sends a one-time heads-up to everyone in the groups you tag below."}
                      </span>
                    </span>
                  </label>
                )}
              </>
            )}
          </div>

          {/* Branding / Theme */}
          <div className="bg-white rounded-xl border border-[#E5E0D8] p-6 mb-6">
            <h2 className="text-lg font-medium text-[#2C302E] mb-4">Branding & Theme</h2>

            {/* Logo Upload */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-[#2C302E] mb-2">Logo</label>
              <div className="flex items-center gap-4">
                {survey.theme.logo_url ? (
                  <div className="relative">
                    <img
                      src={survey.theme.logo_url.startsWith('http') ? survey.theme.logo_url : `${API_URL}${survey.theme.logo_url}`}
                      alt="Logo"
                      className="h-16 object-contain rounded border border-[#E5E0D8] bg-white p-1"
                      data-testid="survey-logo-preview"
                    />
                    <button
                      type="button"
                      onClick={() => setSurvey({ ...survey, theme: { ...survey.theme, logo_url: '' } })}
                      className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full"
                      data-testid="survey-logo-remove"
                      title="Remove logo"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                ) : (
                  <div className="h-16 w-32 border-2 border-dashed border-[#E5E0D8] rounded-lg flex items-center justify-center text-[#8E958C]">
                    <Image size={24} />
                  </div>
                )}
                <label className="flex items-center gap-2 px-4 py-2 border border-[#E5E0D8] rounded-lg cursor-pointer hover:bg-[#F4F1EA] transition-colors">
                  {uploading ? <Loader2 className="animate-spin" size={18} /> : <Upload size={18} />}
                  <span>{uploading ? 'Uploading…' : 'Upload Logo'}</span>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/gif,image/svg+xml,image/webp"
                    onChange={handleLogoUpload}
                    className="hidden"
                    disabled={uploading}
                    data-testid="survey-logo-input"
                  />
                </label>
                <p className="text-xs text-[#8E958C]">PNG, JPG, SVG, WebP up to 5 MB</p>
              </div>
            </div>

            {/* Full theme picker — presets, background pattern, card style, font, live preview */}
            <ThemePicker
              theme={survey.theme}
              onChange={(newTheme) => setSurvey({ ...survey, theme: newTheme })}
            />

            {/* Advanced color overrides */}
            <details className="mt-6 group">
              <summary className="cursor-pointer text-sm text-[#5C635A] hover:text-[#2C302E] flex items-center gap-2">
                <span className="select-none">Fine-tune colors manually</span>
              </summary>
              <div className="grid gap-4 sm:grid-cols-2 mt-4">
                {['primary_color', 'secondary_color', 'background_color', 'text_color'].map((key) => (
                  <div key={key}>
                    <label className="block text-sm font-medium text-[#2C302E] mb-2 capitalize">
                      {key.replace('_', ' ')}
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={survey.theme[key]}
                        onChange={(e) => setSurvey({ ...survey, theme: { ...survey.theme, [key]: e.target.value, preset: 'custom' } })}
                        className="w-10 h-10 rounded border border-[#E5E0D8] cursor-pointer"
                      />
                      <input
                        type="text"
                        value={survey.theme[key]}
                        onChange={(e) => setSurvey({ ...survey, theme: { ...survey.theme, [key]: e.target.value, preset: 'custom' } })}
                        className="flex-1 px-3 py-2 rounded-lg border border-[#E5E0D8] font-mono text-sm"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </details>
          </div>

          {/* Questions */}
          <div className="bg-white rounded-xl border border-[#E5E0D8] p-6 mb-6">
            <h2 className="text-lg font-medium text-[#2C302E] mb-4">Questions</h2>
            
            {survey.questions.length === 0 ? (
              <p className="text-[#5C635A] text-center py-8">No questions yet. Add your first question below.</p>
            ) : (
              <div className="space-y-4 mb-6">
                {survey.questions.map((question, qIndex) => (
                  <div
                    key={question.id}
                    onDragOver={(e) => onDragOver(e, qIndex)}
                    onDrop={(e) => onDrop(e, qIndex)}
                    onDragEnd={onDragEnd}
                    className={[
                      "border rounded-lg p-4 transition-all bg-white",
                      dragIndex === qIndex ? "opacity-50" : "opacity-100",
                      dragOverIndex === qIndex && dragIndex !== qIndex
                        ? "border-[#7A8B7A] ring-2 ring-[#7A8B7A]/30"
                        : "border-[#E5E0D8]",
                    ].join(" ")}
                    data-testid={`question-${qIndex}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex flex-col items-center gap-1">
                        <button
                          type="button"
                          aria-label="Move question up"
                          disabled={qIndex === 0}
                          onClick={() => moveQuestion(qIndex, -1)}
                          className="text-[#8E958C] hover:text-[#2C302E] disabled:opacity-30 disabled:cursor-not-allowed"
                          data-testid={`question-${qIndex}-move-up`}
                        >
                          <ChevronUp size={14} />
                        </button>
                        <div
                          draggable
                          onDragStart={(e) => onDragStart(e, qIndex)}
                          className="text-[#8E958C] cursor-grab active:cursor-grabbing select-none"
                          title="Drag to reorder"
                          data-testid={`question-${qIndex}-drag-handle`}
                        >
                          <GripVertical size={20} />
                        </div>
                        <button
                          type="button"
                          aria-label="Move question down"
                          disabled={qIndex === survey.questions.length - 1}
                          onClick={() => moveQuestion(qIndex, +1)}
                          className="text-[#8E958C] hover:text-[#2C302E] disabled:opacity-30 disabled:cursor-not-allowed"
                          data-testid={`question-${qIndex}-move-down`}
                        >
                          <ChevronDown size={14} />
                        </button>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="px-2 py-0.5 bg-[#F4F1EA] text-[#5C635A] rounded text-xs uppercase">
                            {question.type}
                          </span>
                          <label className="flex items-center gap-1 text-xs text-[#5C635A]">
                            <input
                              type="checkbox"
                              checked={question.required}
                              onChange={(e) => updateQuestion(qIndex, { required: e.target.checked })}
                              className="w-3 h-3"
                            />
                            Required
                          </label>
                        </div>
                        
                        <input
                          type="text"
                          value={question.question}
                          onChange={(e) => updateQuestion(qIndex, { question: e.target.value })}
                          className="w-full px-3 py-2 rounded-lg border border-[#E5E0D8] focus:outline-none focus:ring-2 focus:ring-[#7A8B7A]/20 mb-3"
                          placeholder="Enter your question..."
                          data-testid={`question-text-${qIndex}`}
                        />

                        {(question.type === 'radio' || question.type === 'checkbox') && question.options && (
                          <div className="space-y-2 ml-4">
                            <p className="text-xs text-[#5C635A] mb-2">
                              Options:
                              {question.type === 'checkbox' && (
                                <span className="ml-2 text-[#8E958C]">(respondents can pick more than one)</span>
                              )}
                            </p>
                            {question.options.map((option, oIndex) => (
                              <div key={oIndex} className="flex items-center gap-2">
                                <input
                                  type="text"
                                  value={option.label}
                                  onChange={(e) => updateOption(qIndex, oIndex, { label: e.target.value, value: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                                  className="flex-1 px-2 py-1 rounded border border-[#E5E0D8] text-sm focus:outline-none focus:ring-1 focus:ring-[#7A8B7A]/20"
                                  placeholder="Option label"
                                />
                                <button
                                  type="button"
                                  onClick={() => removeOption(qIndex, oIndex)}
                                  className="text-[#8E958C] hover:text-red-500"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            ))}
                            <button
                              type="button"
                              onClick={() => addOption(qIndex)}
                              className="text-sm text-[#7A8B7A] hover:text-[#637363]"
                            >
                              + Add option
                            </button>
                          </div>
                        )}

                        {(question.type === 'text' || question.type === 'textarea') && (
                          <input
                            type="text"
                            value={question.placeholder || ''}
                            onChange={(e) => updateQuestion(qIndex, { placeholder: e.target.value })}
                            className="w-full px-3 py-2 rounded-lg border border-[#E5E0D8] focus:outline-none focus:ring-1 focus:ring-[#7A8B7A]/20 text-sm"
                            placeholder="Placeholder text..."
                          />
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeQuestion(qIndex)}
                        className="text-[#8E958C] hover:text-red-500"
                        data-testid={`remove-question-${qIndex}`}
                      >
                        <Trash2 size={20} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Add Question Buttons */}
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => addQuestion('radio')}
                className="flex items-center gap-1 px-3 py-2 border border-[#E5E0D8] rounded-lg text-sm text-[#5C635A] hover:bg-[#F4F1EA] transition-colors"
                data-testid="add-radio-question"
              >
                <Plus size={16} />
                Multiple Choice (pick one)
              </button>
              <button
                type="button"
                onClick={() => addQuestion('checkbox')}
                className="flex items-center gap-1 px-3 py-2 border border-[#E5E0D8] rounded-lg text-sm text-[#5C635A] hover:bg-[#F4F1EA] transition-colors"
                data-testid="add-checkbox-question"
              >
                <Plus size={16} />
                Select All That Apply
              </button>
              <button
                type="button"
                onClick={() => addQuestion('star')}
                className="flex items-center gap-1 px-3 py-2 border border-[#E5E0D8] rounded-lg text-sm text-[#5C635A] hover:bg-[#F4F1EA] transition-colors"
                data-testid="add-star-question"
              >
                <Plus size={16} />
                Star Rating
              </button>
              <button
                type="button"
                onClick={() => addQuestion('text')}
                className="flex items-center gap-1 px-3 py-2 border border-[#E5E0D8] rounded-lg text-sm text-[#5C635A] hover:bg-[#F4F1EA] transition-colors"
                data-testid="add-text-question"
              >
                <Plus size={16} />
                Short Answer
              </button>
              <button
                type="button"
                onClick={() => addQuestion('textarea')}
                className="flex items-center gap-1 px-3 py-2 border border-[#E5E0D8] rounded-lg text-sm text-[#5C635A] hover:bg-[#F4F1EA] transition-colors"
                data-testid="add-textarea-question"
              >
                <Plus size={16} />
                Long Answer
              </button>
            </div>
          </div>

          {/* Email Reports */}
          <div className="bg-white rounded-xl border border-[#E5E0D8] p-6 mb-6">
            <h2 className="text-lg font-medium text-[#2C302E] mb-4">Scheduled Email Reports</h2>
            
            <div className="grid gap-4">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="report-enabled"
                  checked={survey.report_schedule.enabled}
                  onChange={(e) => setSurvey({
                    ...survey,
                    report_schedule: { ...survey.report_schedule, enabled: e.target.checked }
                  })}
                  className="w-4 h-4 rounded border-[#E5E0D8] text-[#7A8B7A] focus:ring-[#7A8B7A]"
                />
                <label htmlFor="report-enabled" className="text-sm text-[#2C302E]">Enable scheduled reports</label>
              </div>

              {survey.report_schedule.enabled && (
                <>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#2C302E] mb-2">Frequency</label>
                      <select
                        value={survey.report_schedule.frequency}
                        onChange={(e) => setSurvey({
                          ...survey,
                          report_schedule: { ...survey.report_schedule, frequency: e.target.value }
                        })}
                        className="w-full px-3 py-2 rounded-lg border border-[#E5E0D8] focus:outline-none focus:ring-2 focus:ring-[#7A8B7A]/20"
                      >
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                      </select>
                    </div>

                    {survey.report_schedule.frequency === 'weekly' && (
                      <div>
                        <label className="block text-sm font-medium text-[#2C302E] mb-2">Day of Week</label>
                        <select
                          value={survey.report_schedule.day_of_week}
                          onChange={(e) => setSurvey({
                            ...survey,
                            report_schedule: { ...survey.report_schedule, day_of_week: parseInt(e.target.value) }
                          })}
                          className="w-full px-3 py-2 rounded-lg border border-[#E5E0D8] focus:outline-none focus:ring-2 focus:ring-[#7A8B7A]/20"
                        >
                          <option value={0}>Monday</option>
                          <option value={1}>Tuesday</option>
                          <option value={2}>Wednesday</option>
                          <option value={3}>Thursday</option>
                          <option value={4}>Friday</option>
                          <option value={5}>Saturday</option>
                          <option value={6}>Sunday</option>
                        </select>
                      </div>
                    )}

                    {survey.report_schedule.frequency === 'monthly' && (
                      <div>
                        <label className="block text-sm font-medium text-[#2C302E] mb-2">Day of Month</label>
                        <input
                          type="number"
                          min={1}
                          max={28}
                          value={survey.report_schedule.day_of_month}
                          onChange={(e) => setSurvey({
                            ...survey,
                            report_schedule: { ...survey.report_schedule, day_of_month: parseInt(e.target.value) }
                          })}
                          className="w-full px-3 py-2 rounded-lg border border-[#E5E0D8] focus:outline-none focus:ring-2 focus:ring-[#7A8B7A]/20"
                        />
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-[#2C302E] mb-2">Time (Hour)</label>
                      <select
                        value={survey.report_schedule.hour}
                        onChange={(e) => setSurvey({
                          ...survey,
                          report_schedule: { ...survey.report_schedule, hour: parseInt(e.target.value) }
                        })}
                        className="w-full px-3 py-2 rounded-lg border border-[#E5E0D8] focus:outline-none focus:ring-2 focus:ring-[#7A8B7A]/20"
                      >
                        {Array.from({ length: 24 }, (_, i) => (
                          <option key={i} value={i}>{i.toString().padStart(2, '0')}:00</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#2C302E] mb-2">Recipients</label>
                    <div className="flex gap-2 mb-2">
                      <input
                        type="email"
                        value={newRecipient}
                        onChange={(e) => setNewRecipient(e.target.value)}
                        placeholder="email@example.com"
                        className="flex-1 px-3 py-2 rounded-lg border border-[#E5E0D8] focus:outline-none focus:ring-2 focus:ring-[#7A8B7A]/20"
                      />
                      <button
                        type="button"
                        onClick={addRecipient}
                        className="px-4 py-2 bg-[#7A8B7A] text-white rounded-lg hover:bg-[#637363] transition-colors"
                      >
                        Add
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {survey.report_schedule.recipients.map(email => (
                        <span key={email} className="inline-flex items-center gap-1 px-3 py-1 bg-[#F4F1EA] rounded-full text-sm">
                          {email}
                          <button
                            type="button"
                            onClick={() => removeRecipient(email)}
                            className="text-[#8E958C] hover:text-red-500"
                          >
                            <Trash2 size={14} />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SurveyEditor;
